import { useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import type { TodoProjectResponse } from '@/lib/api';
import { resolveProjectColor } from '../lib/projectColors';

const NONE = '__none__';

interface ProjectPickerProps {
  /** Current projectId, or null for "No project". */
  value: string | null;
  projects: TodoProjectResponse[];
  onChange: (projectId: string | null) => void;
  /** When provided (owner/admin), enables inline "+ New project" creation. */
  onCreateProject?: (name: string) => Promise<TodoProjectResponse>;
  disabled?: boolean;
}

/**
 * Choose the workspace project a task belongs to. Lists existing projects (with
 * their color dot) + "No project"; owners/admins can create one inline without
 * leaving the task. Reused by the shared + personal task editors.
 */
export function ProjectPicker({
  value,
  projects,
  onChange,
  onCreateProject,
  disabled,
}: ProjectPickerProps) {
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name || !onCreateProject) return;
    setSaving(true);
    try {
      const project = await onCreateProject(name);
      onChange(project.projectId);
      setNewName('');
      setCreating(false);
    } finally {
      setSaving(false);
    }
  };

  if (creating) {
    return (
      <div className="flex items-center gap-2">
        <Input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="New project name…"
          className="h-9"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void handleCreate();
            }
            if (e.key === 'Escape') {
              setCreating(false);
              setNewName('');
            }
          }}
        />
        <Button type="button" size="sm" onClick={handleCreate} disabled={saving || !newName.trim()}>
          Add
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={() => {
            setCreating(false);
            setNewName('');
          }}
        >
          Cancel
        </Button>
      </div>
    );
  }

  return (
    <Select
      value={value ?? NONE}
      onValueChange={(v) => {
        if (v === '__new__') setCreating(true);
        else onChange(v === NONE ? null : v);
      }}
      disabled={disabled}
    >
      <SelectTrigger>
        <SelectValue placeholder="No project" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NONE}>No project</SelectItem>
        {projects.map((p) => (
          <SelectItem key={p.projectId} value={p.projectId}>
            <span className="flex items-center gap-2">
              <span
                className={cn(
                  'h-2.5 w-2.5 flex-shrink-0 rounded-full',
                  resolveProjectColor(p.color).dot,
                )}
              />
              {p.name}
            </span>
          </SelectItem>
        ))}
        {onCreateProject && (
          <SelectItem value="__new__">
            <span className="flex items-center gap-2 text-primary">
              <Plus className="h-3.5 w-3.5" />
              New project…
            </span>
          </SelectItem>
        )}
      </SelectContent>
    </Select>
  );
}
